const API_BASE = '/api/v1'

interface ApiOptions extends RequestInit {
  requiresAuth?: boolean
  silent?: boolean  // If true, don't logout on 401 errors (for background polling)
}

/**
 * Custom error class lanciata da apiCall su HTTP non-ok.
 * Espone `status` (numero) e `detail` (FastAPI-style) per gestione granulare lato UI
 * (es. 410 Gone come scadenza naturale e non come errore rosso).
 * Compat: `instanceof Error` resta true, `.message` rimane come prima.
 */
export class ApiError extends Error {
  status: number
  detail: string
  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

export async function apiCall<T>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const { requiresAuth = true, silent = false, ...fetchOptions } = options
  
  // Get token from localStorage
  const authData = localStorage.getItem('auth-storage')
  let token = null
  
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      token = parsed.state?.token
    } catch (e) {
      console.error('Failed to parse auth data:', e)
    }
  }
  
  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
    'Content-Type': 'application/json'
  }
  
  // Add Authorization header if token exists and auth is required
  if (requiresAuth && token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers
  })
  
  if (!response.ok) {
    // Handle 401 Unauthorized - Token expired or invalid
    if (response.status === 401) {
      // For silent/polling requests, don't logout - just throw error silently
      if (silent) {
        throw new Error('Authentication error (silent)')
      }
      
      // Clear authentication data
      localStorage.removeItem('auth-storage')
      
      // Redirect to login page
      window.location.href = '/login'
      
      throw new Error('Session expired. Please login again.')
    }
    
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new ApiError(response.status, error.detail || `HTTP ${response.status}`)
  }
  
  // Handle 204/205 No Content responses (no body to parse)
  if (response.status === 204 || response.status === 205) {
    return null as T
  }
  
  return response.json()
}

export const api = {
  get: <T>(endpoint: string) => apiCall<T>(endpoint, { method: 'GET' }),
  post: <T>(endpoint: string, data?: any) => apiCall<T>(endpoint, { 
    method: 'POST', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  put: <T>(endpoint: string, data?: any) => apiCall<T>(endpoint, { 
    method: 'PUT', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  delete: <T>(endpoint: string) => apiCall<T>(endpoint, { method: 'DELETE' }),
  patch: <T>(endpoint: string, data?: any) => apiCall<T>(endpoint, { 
    method: 'PATCH', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  // Silent methods for background polling - don't trigger logout on 401
  silent: {
    get: <T>(endpoint: string) => apiCall<T>(endpoint, { method: 'GET', silent: true }),
    post: <T>(endpoint: string, data?: any) => apiCall<T>(endpoint, { 
      method: 'POST', 
      body: data ? JSON.stringify(data) : undefined,
      silent: true 
    })
  }
}
